import { Box, Link, Stack, Typography } from '@mui/material'
import { CarrierType, CarrierVisitDirection } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { CarrierTypeIcon } from '@planning/components/CarrierTypeIcon'
import { formatDateTime } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import { DamagedIcon, DangerousIcon, ReeferIcon } from '@tom-ui/ui'
import { isContainerReefer, PATHS } from '@tom-ui/utils'
import _ from 'lodash'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { useNavigate } from 'react-router'
import { IOrderWithVisit } from '../../stores/SelectOrderViewStore'
import { OrderItemChip } from '../OrderItemChip'

type Props = {
  direction: CarrierVisitDirection
  containerData?: IOrderWithVisit
}

const visitDetailsPageLinkMap = new Map([
  [CarrierType.Vessel, PATHS.visits.vesselVisits.url],
  [CarrierType.Train, PATHS.visits.railVisits.url],
  [CarrierType.Universal, undefined],
  [CarrierType.Truck, undefined],
  [undefined, undefined],
])

const VisitHeader: FC<{ direction: CarrierVisitDirection; containerData?: IOrderWithVisit }> = ({
  direction,
  containerData,
}) => {
  const { t } = useTranslate()

  const visitTypeLabel = computed(() => {
    const visit = containerData?.visit

    if (visit && 'number' in visit && visit.identifier) {
      return `${visit.identifier} ${visit.number}`
    }

    if (visit?.identifier) {
      return visit.identifier
    }

    if (containerData?.order?.carrierVisitType) {
      return containerData.order.carrierVisitType
    }

    return t('unknown', 'Unknown')
  }).get()

  return (
    <>
      <Typography variant='body1' data-cy={`${direction}-carrier-visit-identifier`}>
        {visitTypeLabel}
      </Typography>
      <CarrierTypeIcon type={containerData?.order?.carrierVisitType} />
    </>
  )
}

export const ContainerVisitsCardCarrierVisitInfo: FC<Props> = observer(
  ({ containerData, direction }) => {
    const { t } = useTranslate()
    const navigate = useNavigate()
    const { railcarItemStore } = usePlanningStore()

    const isReefer = computed(() => isContainerReefer(containerData?.order?.containerType)).get()

    const displyVisitDateTime = () => {
      if (direction === CarrierVisitDirection.Inbound) {
        if (containerData?.visit?.ata)
          return `${t('ata', 'ATA')}: ${formatDateTime(containerData?.visit?.ata)}`
        else if (containerData?.visit?.eta)
          return `${t('eta', 'ETA')}: ${formatDateTime(containerData?.visit?.eta)}`
        else if (containerData?.order?.carrierVisitType)
          return t('awaitingDropOff', 'Awaiting drop off')
      } else if (direction === CarrierVisitDirection.Outbound) {
        if (containerData?.visit?.atd)
          return `${t('atd', 'ATD')}: ${formatDateTime(containerData?.visit?.atd)}`
        else if (containerData?.visit?.etd)
          return `${t('etd', 'ETD')}: ${formatDateTime(containerData?.visit?.etd)}`
        else if (containerData?.order?.carrierVisitType)
          return t('awaitingPickUp', 'Awaiting pick up')
      }

      return ''
    }

    const visitDetailsPageLink = visitDetailsPageLinkMap.get(containerData?.order?.carrierVisitType)
    const railcarId = containerData?.order?.railcarId ?? ''
    const railcarName = _(railcarItemStore.elements).find(r => r.data.id === railcarId)?.data.name
    const referenceNumber =
      containerData?.order?.numericReleaseReference ??
      containerData?.order?.nonNumericReleaseReference

    return (
      <Box>
        <Stack direction={'row'} spacing={1}>
          {(visitDetailsPageLink && (
            <Link
              component='button'
              variant='body2'
              color='textPrimary'
              underline='hover'
              onClick={() => navigate(`${visitDetailsPageLink}/${containerData?.visit?.id}`)}
            >
              <Stack direction={'row'} spacing={1}>
                <VisitHeader direction={direction} containerData={containerData} />
              </Stack>
            </Link>
          )) || <VisitHeader direction={direction} containerData={containerData} />}
        </Stack>
        <Typography variant='body2'>{displyVisitDateTime()}</Typography>
        {referenceNumber && (
          <Typography variant='caption'>
            {t('ref', 'Ref. No. {referenceNumber}', { referenceNumber })}
          </Typography>
        )}

        {containerData?.order?.carrierVisitType === CarrierType.Train && railcarName && (
          <Typography variant='body2'>
            {t('railcarNo', 'Railcar no')}: {railcarName}
          </Typography>
        )}

        <Stack direction='row' spacing={'6px'} alignItems={'center'} mt={'6px'} maxWidth={'100%'}>
          <Stack direction={'row'} spacing={0.5}>
            {containerData?.order && (
              <OrderItemChip label={(containerData?.order?.isEmpty && 'E') || 'F'}></OrderItemChip>
            )}
            {isReefer && (
              <OrderItemChip
                label={
                  <Typography
                    display={'flex'}
                    gap={'4px'}
                    fontSize={'14px'}
                    sx={{ alignItems: 'center' }}
                    component={'div'}
                    variant='body2'
                  >
                    <ReeferIcon sx={{ height: '16px', width: '16px' }} />
                  </Typography>
                }
              />
            )}
            {containerData?.order?.imoClasses && containerData?.order.imoClasses.length > 0 && (
              <OrderItemChip
                label={
                  <Typography
                    display={'flex'}
                    gap={'4px'}
                    fontSize={'14px'}
                    sx={{ alignItems: 'center' }}
                    component={'div'}
                    variant='body2'
                  >
                    <DangerousIcon sx={{ height: '16px', width: '16px', marginRight: '4px' }} />
                    {containerData?.order?.imoClasses.join(', ')}
                  </Typography>
                }
              ></OrderItemChip>
            )}
            {containerData?.order?.isDamaged && (
              <OrderItemChip
                label={
                  <Typography
                    display={'flex'}
                    gap={'4px'}
                    fontSize={'14px'}
                    sx={{ alignItems: 'center' }}
                    component={'div'}
                    variant='body2'
                  >
                    <DamagedIcon sx={{ height: '16px', width: '16px' }} />
                  </Typography>
                }
              />
            )}
            {!!containerData?.order?.grossWeight && (
              <OrderItemChip
                label={`${t('gwt', 'GWT')}: ${containerData?.order?.grossWeight.toLocaleString(undefined, { maximumFractionDigits: 0 })}kg`}
              />
            )}
            {!!containerData?.order?.vgm && (
              <OrderItemChip
                label={`${t('vgm', 'VGM')}: ${containerData?.order?.vgm.toLocaleString(undefined, { maximumFractionDigits: 0 })}kg`}
              />
            )}
          </Stack>
        </Stack>
      </Box>
    )
  },
)
